import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2, Shield, Users, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { staffService } from '../../services/staffService';
import { customToast } from '../../utils/customToast';
import { StaffMember } from '../../types';
import AddStaffModal from '../modals/AddStaffModal';
import EditStaffModal from '../modals/EditStaffModal';
import StaffPermissionsModal from '../modals/StaffPermissionsModal';
import ConfirmationModal from '../ConfirmationModal';

const StaffManagementTab: React.FC = () => {
    const { theme } = useTheme();
    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadStaff();
    }, []);

    const loadStaff = async (showRefreshIndicator = false) => {
        try {
            if (showRefreshIndicator) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            const data = await staffService.getStaff();
            setStaff(data);
        } catch (error: any) {
            console.error('Failed to load staff:', error);
            customToast.error(error.response?.data?.message || 'Failed to load staff members');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleDeleteStaff = async () => {
        if (!selectedStaff) return;

        try {
            setDeleting(true);
            await staffService.deleteStaff(selectedStaff.id);
            customToast.success(`${selectedStaff.full_name} has been removed`);
            setShowDeleteModal(false);
            setSelectedStaff(null);
            loadStaff();
        } catch (error: any) {
            console.error('Failed to delete staff:', error);
            customToast.error(error.response?.data?.message || 'Failed to removed staff member');
        } finally {
            setDeleting(false);
        }
    };

    const getRoleBadgeColor = (roleType: string) => {
        switch (roleType) {
            case 'manager':
                return theme === 'dark' ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700';
            case 'seller':
                return theme === 'dark' ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700';
            case 'worker':
                return theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
            default:
                return theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
        }
    };

    const getStatusBadgeColor = (isActive: boolean) => {
        return isActive
            ? theme === 'dark' ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
            : theme === 'dark' ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700';
    };

    const countActivePermissions = (member: StaffMember) => {
        return member.permissions?.filter(p => p.can_view).length || 0;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Staff Management
                    </h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Manage your shop staff and their access permissions
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => loadStaff(true)}
                        disabled={refreshing}
                        className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${theme === 'dark'
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } disabled:opacity-50`}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add Staff
                    </button>
                </div>
            </div>

            {/* Staff List */}
            {staff.length === 0 ? (
                <div className={`text-center py-12 rounded-lg border-2 border-dashed ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-300 bg-gray-50'
                    }`}>
                    <Users className={`w-12 h-12 mx-auto mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h4 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        No Staff Members Yet
                    </h4>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                        Add staff members to help manage your shop
                    </p>
                </div>
            ) : (
                <div className={`overflow-hidden rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                        Staff Member
                                    </th>
                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                        Role
                                    </th>
                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                        Status
                                    </th>
                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                        Permissions
                                    </th>
                                    <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {staff.map((member) => (
                                    <tr
                                        key={member.id}
                                        className={theme === 'dark' ? 'bg-gray-800/50 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'}
                                    >
                                        <td className="px-4 py-4">
                                            <div className="flex items-center">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                                                    }`}>
                                                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                                        }`}>
                                                        {member.full_name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="ml-3">
                                                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                        {member.full_name}
                                                    </p>
                                                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                        }`}>
                                                        {member.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${getRoleBadgeColor(member.staff_role_type)
                                                }`}>
                                                {member.staff_role_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(member.is_active)
                                                }`}>
                                                {member.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                                {countActivePermissions(member)} modules
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedStaff(member);
                                                        setShowPermissionsModal(true);
                                                    }}
                                                    title="Manage Permissions"
                                                    className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                                                        ? 'text-purple-400 hover:bg-purple-900/30'
                                                        : 'text-purple-600 hover:bg-purple-100'
                                                        }`}
                                                >
                                                    <Shield className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedStaff(member);
                                                        setShowEditModal(true);
                                                    }}
                                                    title="Edit Staff"
                                                    className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                                                        ? 'text-blue-400 hover:bg-blue-900/30'
                                                        : 'text-blue-600 hover:bg-blue-100'
                                                        }`}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedStaff(member);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    title="Remove Staff"
                                                    className={`p-2 rounded-lg transition-colors ${theme === 'dark'
                                                        ? 'text-red-400 hover:bg-red-900/30'
                                                        : 'text-red-600 hover:bg-red-100'
                                                        }`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modals */}
            <AddStaffModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={() => loadStaff()}
            />

            <EditStaffModal
                isOpen={showEditModal}
                staff={selectedStaff}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedStaff(null);
                }}
                onSuccess={() => loadStaff()}
            />

            <StaffPermissionsModal
                isOpen={showPermissionsModal}
                staff={selectedStaff}
                onClose={() => {
                    setShowPermissionsModal(false);
                    setSelectedStaff(null);
                }}
                onSuccess={() => loadStaff()}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedStaff(null);
                }}
                onConfirm={handleDeleteStaff}
                title="Remove Staff Member"
                message={`Are you sure you want to remove ${selectedStaff?.full_name}? They will no longer be able to access your shop.`}
                confirmText="Remove"
                type="danger"
                isLoading={deleting}
            />
        </div>
    );
};

export default StaffManagementTab;
